import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Package, Clock, ArrowRight, Loader2, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { apiRequest, formatPrice } from '../lib/utils';
import { useCart } from '../context/CartContext';

const GOOGLE_REVIEW_URL = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent('Tasty Bites, Via Luigi Lavizzari 5, 6900 Lugano');

const OrderSuccessPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentChecking, setPaymentChecking] = useState(false);
  const { clearCart } = useCart();

  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    const checkPayment = async () => {
      if (sessionId) {
        setPaymentChecking(true);
        try {
          let attempts = 0;
          const maxAttempts = 5;
          const pollStatus = async () => {
            const status = await apiRequest(`/payments/status/${sessionId}`);
            if (status.payment_status === 'paid' || attempts >= maxAttempts) {
              setPaymentChecking(false);
              if (status.payment_status === 'paid') {
                await clearCart();
              }
              fetchOrder();
            } else {
              attempts++;
              setTimeout(pollStatus, 2000);
            }
          };
          await pollStatus();
        } catch (error) {
          console.error('Error checking payment:', error);
          setPaymentChecking(false);
          fetchOrder();
        }
      } else if (orderId) {
        await clearCart();
        fetchOrder();
      }
    };

    const fetchOrder = async () => {
      try {
        const orderIdToFetch = orderId || sessionId;
        if (!orderIdToFetch) { setLoading(false); return; }
        if (orderId) {
          const orderData = await apiRequest(`/orders/${orderId}`);
          setOrder(orderData);
        }
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    checkPayment();
  }, [sessionId, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || paymentChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#FFC107] mx-auto mb-4" />
          <p className="text-zinc-400">
            {paymentChecking ? t('orderSuccess.confirmingPayment') : t('orderSuccess.loadingOrder')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16" data-testid="order-success-page">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 mb-6">
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
          <h1 className="text-4xl font-bold uppercase tracking-tight text-white mb-2 font-['Barlow_Condensed']">
            {t('orderSuccess.title')} <span className="text-[#FFC107]">{t('orderSuccess.titleHighlight')}</span>
          </h1>
          <p className="text-zinc-400 text-lg">{t('orderSuccess.subtitle')}</p>
        </div>

        {order && (
          <Card className="bg-[#141417] border-[#27272A] mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#27272A]">
                <div>
                  <p className="text-sm text-zinc-400">{t('orderSuccess.orderId')}</p>
                  <p className="font-mono text-lg">{order.id?.slice(0, 8)}...</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-zinc-400">{t('orderSuccess.payment')}</p>
                  <p className={`font-semibold ${order.payment_status === 'paid' ? 'text-green-500' : 'text-yellow-500'}`}>
                    {order.payment_status === 'paid' ? t('orders.paid') : (order.order_type === 'pickup' ? t('orderSuccess.pendingPickup') : t('orderSuccess.pendingCod'))}
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div>
                      <p className="text-white">{item.name}</p>
                      <p className="text-sm text-zinc-400">{t('orderSuccess.qty')}: {item.quantity}</p>
                    </div>
                    <p className="text-[#FFC107]">{formatPrice(item.price * item.quantity)}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#27272A] pt-4 space-y-2">
                {order.subtotal && (
                  <div className="flex justify-between text-zinc-400">
                    <span>{t('orderSuccess.subtotal')}</span>
                    <span>{formatPrice(order.subtotal)}</span>
                  </div>
                )}
                {order.delivery_fee !== undefined && order.order_type === 'delivery' && (
                  <div className="flex justify-between text-zinc-400">
                    <span>{t('orderSuccess.deliveryFee')}</span>
                    <span className={order.delivery_fee === 0 ? 'text-green-500' : ''}>
                      {order.delivery_fee === 0 ? t('orderSuccess.free') : formatPrice(order.delivery_fee)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold">
                  <span>{t('orderSuccess.total')}</span>
                  <span className="text-[#FFC107]">{formatPrice(order.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-[#141417] border-[#27272A] mb-8">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">{t('orderSuccess.orderStatus')}</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="font-medium text-white">{t('orderSuccess.orderConfirmed')}</p>
                  <p className="text-sm text-zinc-400">{t('orderSuccess.orderReceived')}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#1C1C22] flex items-center justify-center">
                  <Clock className="h-5 w-5 text-zinc-500" />
                </div>
                <div>
                  <p className="font-medium text-zinc-400">{t('orderSuccess.preparingMeal')}</p>
                  <p className="text-sm text-zinc-500">{t('orderSuccess.estimatedTime')}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#1C1C22] flex items-center justify-center">
                  <Package className="h-5 w-5 text-zinc-500" />
                </div>
                <div>
                  <p className="font-medium text-zinc-400">{order?.order_type === 'pickup' ? t('orderSuccess.readyForPickup') : t('orderSuccess.outForDelivery')}</p>
                  <p className="text-sm text-zinc-500">{order?.order_type === 'pickup' ? t('orderSuccess.pickupAtStore') : t('orderSuccess.onTheWay')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center my-8 space-y-2" data-testid="order-thank-you">
          <p className="text-zinc-300 text-sm">{t('orderSuccess.thankYouMsg')}</p>
          <p className="text-zinc-400 text-sm">{t('orderSuccess.followMsg')}</p>
          <p className="text-[#FFC107] font-bold text-lg mt-2">Team Tasty Bites Lugano</p>
        </div>

        {(!order || order.order_type === 'pickup') && (
          <Card className="bg-[#FFC107]/5 border-[#FFC107]/30 mb-8" data-testid="rate-experience-card">
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-[#FFC107] fill-[#FFC107]" />
                ))}
              </div>
              <h3 className="font-semibold text-white mb-1">{t('orderSuccess.rateTitle', 'Rate your experience')}</h3>
              <p className="text-zinc-400 text-sm mb-4">{t('orderSuccess.rateSubtitle', 'Enjoyed your pickup? A quick Google review helps us a lot!')}</p>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-[#FFC107] text-black font-bold uppercase tracking-wider text-xs hover:bg-[#F59E0B] transition-colors active:scale-[0.98]"
                data-testid="rate-experience-button"
              >
                <Star className="h-4 w-4 fill-black" />
                {t('contact.rateUs', 'Rate us on Google')}
              </a>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/orders" className="flex-1">
            <Button variant="outline" className="w-full h-12 border-zinc-700 hover:bg-[#25252E]" data-testid="view-orders-button">
              {t('orderSuccess.viewAllOrders')}
            </Button>
          </Link>
          <Link to="/menu" className="flex-1">
            <Button className="w-full h-12 bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold uppercase tracking-wide" data-testid="order-more-button">
              {t('orderSuccess.orderMore')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
